import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { EnvVariable, EnvVariableScope } from '../types/envVariables';
import { validateKey, validateValue } from '../services/envVariableService';
import { envVariableIdentityKey, normalizeEnvVariableScope } from '../utils/executionEnvironment';

interface EnvVariableDialogProps {
  open: boolean;
  variable: EnvVariable | null;
  existingIdentities: string[];
  onClose: () => void;
  onSave: (variable: EnvVariable, options?: { previousKey?: string; previousScope?: EnvVariableScope }) => void;
}

const EnvVariableDialog: React.FC<EnvVariableDialogProps> = ({
  open,
  variable,
  existingIdentities,
  onClose,
  onSave,
}) => {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<EnvVariableScope>('environment');
  const [showValue, setShowValue] = useState(false);
  const [errors, setErrors] = useState<{ key?: string; value?: string }>({});
  const isEditing = variable !== null;

  useEffect(() => {
    if (variable) {
      setKey(variable.key);
      setValue(variable.value);
      setDescription(variable.description || '');
      setScope('environment');
    } else {
      setKey('');
      setValue('');
      setDescription('');
      setScope('environment');
    }
    setErrors({});
    setShowValue(false);
  }, [variable, open]);

  const validate = (): boolean => {
    const newErrors: { key?: string; value?: string } = {};

    // Validate key
    const keyValidation = validateKey(key);
    if (!keyValidation.valid) {
      newErrors.key = keyValidation.error;
    } else if (isEditing) {
      const ownIdentity = envVariableIdentityKey(variable!.key, variable!.scope);
      const nextIdentity = envVariableIdentityKey(key, scope);
      if (nextIdentity !== ownIdentity && existingIdentities.includes(nextIdentity)) {
        newErrors.key = 'A variable with this key already exists';
      }
    } else if (existingIdentities.includes(envVariableIdentityKey(key, scope))) {
      newErrors.key = 'A variable with this key already exists';
    }

    // Validate value
    const valueValidation = validateValue(value);
    if (!valueValidation.valid) {
      newErrors.value = valueValidation.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    const now = new Date().toISOString();
    const envVariable: EnvVariable = {
      key,
      value,
      scope,
      description,
      createdAt: variable?.createdAt || now,
      updatedAt: now,
    };

    const previousScope = variable ? normalizeEnvVariableScope(variable.scope) : null;
    const options =
      isEditing && (variable!.key !== key || previousScope !== scope)
        ? { previousKey: variable!.key, previousScope: previousScope! }
        : undefined;
    onSave(envVariable, options);
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow any characters (same as CSV import); only reject empty on save via validateKey
    const newKey = e.target.value;
    setKey(newKey);
    if (errors.key) {
      setErrors({ ...errors, key: undefined });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      hideBackdrop
      scroll="paper"
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 32px)', sm: 440 },
          maxWidth: 'min(92vw, 520px)',
          maxHeight: 'calc(100vh - 160px)',
          m: 2,
          borderRadius: '14px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle sx={{ flexShrink: 0, pb: 1.25, fontWeight: 700, fontSize: '1.05rem' }}>
        {isEditing ? 'Edit Environment Variable' : 'Add Environment Variable'}
      </DialogTitle>
      
      <DialogContent
        sx={{
          pt: '20px !important',
          overflowY: 'auto',
          flex: '1 1 auto',
        }}
      >
        <Box>
          {/* Key Field */}
          <TextField
            fullWidth
            label="Key"
            value={key}
            onChange={handleKeyChange}
            error={!!errors.key}
            helperText={errors.key || 'Any characters allowed (same as CSV import)'}
            sx={{ mb: 1.5 }}
            size="small"
            placeholder="API_KEY"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Key naming: any characters are allowed, including special symbols (same as CSV import)">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {/* Value Field */}
          <TextField
            fullWidth
            label="Value"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (errors.value) {
                setErrors({ ...errors, value: undefined });
              }
            }}
            error={!!errors.value}
            helperText={errors.value || 'The actual value to substitute in requests'}
            type={showValue ? 'text' : 'password'}
            sx={{ mb: 1.5 }}
            size="small"
            placeholder="Enter sensitive value (password, API key, etc.)"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowValue(!showValue)}
                    edge="end"
                  >
                    {showValue ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel id="env-var-scope-label">Scope</InputLabel>
            <Select
              labelId="env-var-scope-label"
              label="Scope"
              value={scope}
              onChange={(e) => setScope(normalizeEnvVariableScope(String(e.target.value)))}
            >
              <MenuItem value="environment">Environment (this .Env profile only)</MenuItem>
            </Select>
          </FormControl>

          {/* Description Field */}
          <TextField
            fullWidth
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            placeholder="Brief description of this variable"
            helperText="Optional note about what this variable is used for"
            sx={{ mb: 1 }}
            size="small"
          />

          {/* Usage Example */}
          {key && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
              <strong>Usage in requests:</strong>
              <Box 
                component="code" 
                sx={{ 
                  display: 'block', 
                  mt: 1, 
                  p: 1, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}
              >
                {`{{"${key}"}}`}
              </Box>
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ flexShrink: 0, px: 2, pb: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', height: 36, minHeight: 36 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!key || !value}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            height: 36,
            minHeight: 36,
            bgcolor: '#1d4ed8',
            '&:hover': { bgcolor: '#1e40af' },
          }}
        >
          {isEditing ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnvVariableDialog;
